// src/services/googlePlaces.js
import { calculateLocationDistance, getCategoriesFromTypes, getCategoriesForPlace } from '@/utils/journeyUtils'
import { trackException } from '@/services/analytics'
import { useAppStore } from '@/stores/app'
import { useDataStore } from '@/stores/data'

// Convert Google Places prediction to match the expected format
export const resultToViewModel = (prediction) => ({
	id: prediction.place_id,
	title: prediction.structured_formatting.main_text,
	subTitle: prediction.structured_formatting.secondary_text,
	type: getPlaceType(prediction.types),
	categories: getCategoriesFromTypes(prediction.types, prediction, 'resultToViewModel'),
	placeTypes: prediction.types,
})

// Helper function to map Google place types to expected types
export const getPlaceType = (types = []) => {
	if (types.includes('train_station')) return 'train'
	if (types.includes('subway_station')) return 'subway'
	if (types.includes('transit_station')) return 'station'
	if (types.includes('establishment')) return 'poi'
	if (types.includes('street_address') || types.includes('route')) return 'address'
	if (types.includes('locality') || types.includes('sublocality')) return 'place'
	if (types.includes('plus_code')) return 'pluscode'
	return 'address'
}

// Format address from components
export const formatAddress = (components = []) => {
	const postal = components.find((c) => c.types.includes('postal_code'))
	const city = components.find((c) => c.types.includes('locality'))
	const country = components.find((c) => c.types.includes('country'))
	return [postal?.long_name, city?.long_name, country?.long_name].filter(Boolean).join(', ')
}

// Generate a session token
export const generateSessionToken = () => {
	return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) => (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16))
}

export class GooglePlacesService {
	constructor(apiKey = 'AIzaSyAPuw9_dcPYYBGREFrZ5-O6hGd1Hz8iDR4') {
		this.apiKey = apiKey
		this.placesService = null
		this.autocompleteService = null
		this.directionsService = null
		this.sessionToken = null
		this.googleMap = null
		this.googleMapsCallback = 'initGoogleCallback'
		//this.proxyUrl = 'http://localhost:3000/proxy/'
		this.proxyUrl = 'https://rvu-api.kogenta.com/api/proxy/'
	}

	async init() {
		try {
			// Load Google Maps script if not already loaded
			if (!window.google?.maps?.places) {
				await this.loadGoogleMapsScript()
			}
			this.directionsService = new window.google.maps.DirectionsService()
			this.sessionToken = this.generateSessionToken()
		} catch (error) {
			console.error('Failed to initialize Google Places:', error)
			throw error
		}
	}

	loadGoogleMapsScript() {
		return new Promise((resolve, reject) => {
			// If already loaded, resolve immediately
			if (window.google?.maps?.places) {
				resolve()
				return
			}

			// Create callback function
			window[this.googleMapsCallback] = () => {
				resolve()
				// Clean up callback
				delete window[this.googleMapsCallback]
			}

			// Load the script
			const script = document.createElement('script')
			script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places&loading=async&callback=${this.googleMapsCallback}`
			script.async = true
			script.crossOrigin = 'anonymous'

			script.onerror = () => {
				reject(new Error('Failed to load Google Maps script'))
				// Clean up callback in case of error
				delete window[this.googleMapsCallback]

				const appStore = useAppStore()
				const dataStore = useDataStore()
				const meta = {
					type: 'script_load_error',
					source: script.src,
				}
				trackException(new Error('Failed to load Google Maps script'), meta, appStore, dataStore)
			}

			document.head.appendChild(script)
		})
	}

	generateSessionToken() {
		return generateSessionToken()
	}

	async search(query, location = null) {
		try {
			// Make sure we have an API key
			if (!this.apiKey) {
				throw new Error('Google Places API key is required')
			}

			const url = this.proxyUrl + 'https://places.googleapis.com/v1/places:autocomplete'
			const headers = {
				'Content-Type': 'application/json',
				'X-Goog-Api-Key': this.apiKey,
				'X-Goog-FieldMask': '*',
			}

			// Add session token if available
			if (this.sessionToken) {
				headers['X-Goog-SessionToken'] = this.sessionToken
			}

			const body = {
				input: query,
				includedRegionCodes: ['no', 'se'],
				includeQueryPredictions: true,
				languageCode: 'no',
				regionCode: 'NO',
				sessionToken: this.sessionToken,
			}

			// Add location bias if coordinates are provided
			if (location && location.latitude && location.longitude) {
				body.locationBias = {
					circle: {
						center: {
							latitude: location.latitude,
							longitude: location.longitude,
						},
						radius: 500.0, // 500 meters
					},
				}
			}

			console.warn('Places API v1 request:', body)

			const response = await fetch(url, {
				method: 'POST',
				headers: headers,
				body: JSON.stringify(body),
			})

			if (!response.ok) {
				throw new Error(`Places API returned status: ${response.status}`)
			}

			const data = await response.json()
			console.warn('Places API v1 results:', data)

			if (!data.suggestions || !Array.isArray(data.suggestions)) {
				return []
			}

			// Map the new API response to the expected view model format
			const mappedPredictions = data.suggestions.map((suggestion) => {
				var place = suggestion.placePrediction
				if (!place) return {}
				return {
					id: place.placeId,
					title: place?.structuredFormat?.mainText?.text || '',
					subTitle: place?.structuredFormat?.secondaryText?.text || '',
					placeId: place.placeId,
					type: getPlaceType(place.types),
					categories: getCategoriesFromTypes(place.types, place?.text?.text, 'search'),
					placeTypes: place.types,
				}
			})
			console.warn('Mapped predictions:', mappedPredictions)
			return mappedPredictions
		} catch (error) {
			console.error('Places Autocomplete error:', error)
			return []
		}
	}

	async getPlaceForLocation(location, returnAll = false) {
		const url = this.proxyUrl + 'https://places.googleapis.com/v1/places:searchNearby'
		const data = {
			maxResultCount: 5,
			//includedTypes: ['premise', 'street_address'],
			languageCode: 'no',
			regionCode: 'NO',
			locationRestriction: {
				circle: {
					center: {
						latitude: location.latitude,
						longitude: location.longitude,
					},
					radius: 100.0,
				},
			},
		}
		try {
			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-Goog-Api-Key': this.apiKey,
					'X-Goog-SessionToken': this.sessionToken,
					'X-Goog-FieldMask': 'places.id,places.displayName,places.primaryType,places.types,places.formattedAddress,places.location,places.addressComponents',
				},
				body: JSON.stringify(data),
			})
			if (!response.ok) {
				throw new Error(`Places API returned status: ${response.status}`)
			}
			const result = await response.json()
			console.warn('NearbySearch ALL:', result)

			if (result?.places?.length) {
				// Process all places to normalized format
				const normalizedPlaces = result.places.map((place) => {
					// Calculate distance
					const distance = calculateLocationDistance(location, place.location)

					// Extract address components
					const streetNumber = place.addressComponents.find((c) => c?.types?.includes('street_number'))?.longText ?? ''
					const streetName = place.addressComponents.find((c) => c?.types?.includes('route'))?.longText ?? ''
					const postalCode = place.addressComponents.find((c) => c?.types?.includes('postal_code'))?.longText ?? ''
					const postalTown = place.addressComponents.find((c) => c?.types?.includes('postal_town'))?.longText ?? ''
					const locality = place.addressComponents.find((c) => c?.types?.includes('locality'))?.longText ?? ''

					// Format postal address
					let postalAddress = postalCode + ' ' + postalTown
					if (locality && !postalTown) postalAddress += ' ' + locality

					// Return normalized place object
					return {
						id: place.id,
						place_id: place.id,
						title: place.displayName.text,
						subTitle: place.formattedAddress,
						type: getPlaceType(place.types),
						categories: getCategoriesFromTypes(place.types, place.displayName?.text, 'getPlaceForLocation'),
						placeTypes: place.types,
						latitude: place.location.latitude,
						longitude: place.location.longitude,
						addressComponents: place.addressComponents,
						streetAddress: streetName + ' ' + streetNumber,
						postalAddress: postalAddress,
						distance: distance,
					}
				})

				// Log and return based on returnAll parameter
				if (returnAll) {
					console.warn('NearbySearch RESULT (All):', normalizedPlaces)
					return normalizedPlaces
				} else {
					const firstPlace = normalizedPlaces[0]
					console.warn('NearbySearch FIRST:', result.places[0])
					console.warn('NearbySearch RESULT:', firstPlace)
					return firstPlace
				}
			} else {
				console.warn('NearbySearch NO RESULTS', result)
			}
		} catch (error) {
			console.error('Nearby Search error', error)
		}

		// Default to coordinates if no places found
		const defaultResponse = {
			latitude: location.latitude,
			longitude: location.longitude,
			title: `${location.latitude.toFixed(5)}, ${location.longitude.toFixed(5)}`,
			subTitle: 'GPS-koordinater',
			type: 'gps',
		}

		return returnAll ? [defaultResponse] : defaultResponse
	}

	async getPlaceDetails(place) {
		// Only request the fields you were using before
		const fields = ['name', 'formatted_address', 'address_components', 'location', 'types'].join(',')

		// Use either the new place_id or the old id
		const placeId = place.place_id || place.id

		// Return early if no placeId
		if (!placeId) {
			return null
		}

		// Construct the request URL
		const url = `${this.proxyUrl}https://places.googleapis.com/v1/places/${placeId}?fields=${fields}&key=${this.apiKey}&languageCode=no&sessionToken=${this.sessionToken}`

		try {
			// This seems to trigger a CORS error
			const headers = { 'Content-Type': 'application/json' }
			if (this.sessionToken) headers['X-Goog-SessionToken'] = this.sessionToken

			const response = await fetch(url, {
				method: 'GET',
				headers: headers,
			})

			if (!response.ok) {
				const error = new Error(`Places API error: ${response.status} ${response.statusText} - placeId: ${placeId}`)
				error.object = place
				throw error
			}

			const data = await response.json()

			// Add details to the place object
			place.latitude = data.location.latitude
			place.longitude = data.location.longitude
			place.types = getPlaceType(data.types)
			place.categories = getCategoriesFromTypes(data.types, place.title, 'getPlaceDetails')
			place.placeTypes = data.types
			place.formattedAddress = data.formattedAddress
			place.addressComponents = data.addressComponents

			return place
		} catch (error) {
			console.error('Place Details v1 API error:', error)
			throw error
		}
	}

	formatAddress(components = []) {
		return formatAddress(components)
	}

	async getPlaceOrAddressForLocation(location) {
		var places = await googlePlacesService.getPlaceForLocation(location, true)
		var address = await googlePlacesService.getAddressForLocation(location)
		// Check if any of the places match the address
		var place = places.find((p) => p.streetAddress == address.streetAddress && p.postalAddress == address.postalAddress)
		if (place) {
			console.warn('getPlaceOrAddressForLocation found matching place:', place)
			return place
		}
		return address
	}

	async getAddressForLocation(location) {
		console.warn('geocodeLocation', location)

		if (!this.apiKey) {
			throw new Error('Google Places API key is required')
		}

		let nearestAddress = null

		try {
			// Construct the geocoding request URL
			const url = `${this.proxyUrl}https://maps.googleapis.com/maps/api/geocode/json?latlng=${location.latitude},${location.longitude}&key=${this.apiKey}&language=no`

			const response = await fetch(url, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
				},
			})

			if (!response.ok) {
				throw new Error(`Geocoding API returned status: ${response.status}`)
			}

			const data = await response.json()
			if (data.status === 'OK' && data.results && data.results.length > 0) {
				// Add distance to all results
				data.results.forEach((result) => {
					result.distance = calculateLocationDistance(location, {
						latitude: result.geometry.location.lat,
						longitude: result.geometry.location.lng,
					})
				})

				const firstResult = data.results[0]
				console.warn('Geocoding ALL:', data.results)

				// Extract address components with defaults to empty strings
				const plusCode = firstResult.address_components.find((c) => c.types.includes('plus_code'))?.long_name ?? ''
				const streetNumber = firstResult.address_components.find((c) => c.types.includes('street_number'))?.long_name ?? ''
				const streetName = firstResult.address_components.find((c) => c.types.includes('route'))?.long_name ?? ''
				const postalCode = firstResult.address_components.find((c) => c.types.includes('postal_code'))?.long_name ?? ''
				const postalTown = firstResult.address_components.find((c) => c.types.includes('postal_town'))?.long_name ?? ''
				const locality = firstResult.address_components.find((c) => c.types.includes('locality'))?.long_name ?? '' // Note: fixed 'longText' to 'long_name'
				const countryCode = firstResult.address_components.find((c) => c.types.includes('country'))?.short_name ?? ''
				const outsideNorway = countryCode != 'NO'

				// Construct title with trimming and fallback
				let title = (streetName + ' ' + streetNumber).trim()
				if (!title) {
					title = firstResult.formatted_address.replace(plusCode, '').trim() || 'Unknown address'
				}

				// Construct subtitle with trimming and fallback
				let subtitle = (postalCode + ' ' + postalTown).trim()
				if (!subtitle && locality) {
					subtitle = locality
				}

				// Get a single place type from the result
				let type = getPlaceType(firstResult.types)

				// Special handling for addresses outside Norway
				if (outsideNorway && type != 'pluscode') {
					var formatedTitleParts = firstResult.formatted_address.split(',')
					if (formatedTitleParts.length > 1) {
						title = formatedTitleParts[0]
						subtitle = formatedTitleParts.slice(1).join(',').trim()
					}
				}

				// Calculate distance
				const distance = calculateLocationDistance(location, {
					latitude: firstResult.geometry.location.lat,
					longitude: firstResult.geometry.location.lng,
				})

				nearestAddress = {
					id: firstResult.place_id,
					place_id: firstResult.place_id,
					title: title,
					subTitle: subtitle,
					type: type,
					categories: getCategoriesFromTypes(firstResult.types, firstResult.formatted_address, 'getAddressForLocation'),
					placeTypes: firstResult.types,
					latitude: firstResult.geometry.location.lat,
					longitude: firstResult.geometry.location.lng,
					addressComponents: firstResult.address_components,
					formattedAddress: firstResult.formatted_address,
					streetAddress: (streetName + ' ' + streetNumber).trim(),
					postalAddress: (postalCode + ' ' + postalTown).trim(), // Updated for consistency
					distance: distance,
					plusCode: plusCode,
				}

				return nearestAddress
			}
		} catch (error) {
			console.error('getAddressForLocation error:', error)
		}

		var gps_title = ''
		var gps_id = ''
		if (nearestAddress?.type == 'pluscode') {
			gps_title = nearestAddress.title
			gps_id = nearestAddress.id
		}

		// Fallback to GPS coordinates if geocoding fails or no results
		return {
			id: gps_id,
			place_id: gps_id,
			title: gps_title,
			latitude: location.latitude,
			longitude: location.longitude,
			subTitle: 'GPS-koordinat',
			type: 'gps',
		}
	}

	async getDirectionsForTrip(trip) {
		console.warn('getDirectionsForTrip', trip)

		if (!trip || !trip.origin || !trip.destination || !trip.activity_name) return null

		// Map activity to travel mode
		let travelMode = google.maps.TravelMode.TRANSIT // Default to transit - Tram, Bus, Subway, Train, Boat, Flights
		if (trip.activity_name == 'automotive') travelMode = google.maps.TravelMode.DRIVING
		if (trip.activity_name == 'cycling') travelMode = google.maps.TravelMode.BICYCLING
		if (trip.activity_name == 'walking') travelMode = google.maps.TravelMode.WALKING
		if (trip.activity_name == 'micro') travelMode = google.maps.TravelMode.TWO_WHEELER

		// Configure request
		const request = {
			origin: new google.maps.LatLng(trip.origin.latitude, trip.origin.longitude),
			destination: new google.maps.LatLng(trip.destination.latitude, trip.destination.longitude),
			travelMode: travelMode,
		}

		// use the directions service to get the route
		return new Promise((resolve, reject) => {
			this.directionsService.route(request, (result, status) => {
				if (status === google.maps.DirectionsStatus.OK) {
					console.warn('getDirectionsForTrip', result)
					resolve(result)
				} else {
					console.warn('getDirectionsForTrip error', status)
					reject(status)
				}
			})
		})
	}
}

// Create and export a singleton instance
export const googlePlacesService = new GooglePlacesService()
export default googlePlacesService
